// Delade sitemap-helpers. Håller XML-bygget på ett ställe så varje
// sub-sitemap-route bara behöver definiera sina entries.

import { BASE_URL } from "@/lib/seo/constants";
import { isPausedPhotoPath } from "@/lib/paused-photo";
import { isConsolidated } from "@/lib/seo/canonical-overrides";
import { CONTENT_DATES, CLUSTER_DATES } from "@/lib/seo/content-dates.generated";

export interface SitemapEntry {
  path: string;
  lastmod?: string;
  changefreq?:
    | "always"
    | "hourly"
    | "daily"
    | "weekly"
    | "monthly"
    | "yearly"
    | "never";
  priority?: string;
}

export function renderUrlset(entries: SitemapEntry[]) {
  // Dubbletter (samma path från flera källor, eller med/utan avslutande slash)
  // gör att Google räknar sitemapen som slarvig. Första posten vinner.
  // Stödsidor som pekar canonical mot en annan URL utelämnas helt — en sitemap
  // ska bara innehålla primära sidor.
  const seen = new Set<string>();
  const unique = entries.filter((e) => {
    const key = e.path.length > 1 ? e.path.replace(/\/$/, "") : e.path;
    if (seen.has(key)) return false;
    if (isConsolidated(key) || isPausedPhotoPath(key)) return false;
    seen.add(key);
    return true;
  });
  const urls = unique.map((e) =>
    [
      `  <url>`,
      `    <loc>${BASE_URL}${e.path}</loc>`,
      e.lastmod ? `    <lastmod>${e.lastmod}</lastmod>` : null,
      e.changefreq ? `    <changefreq>${e.changefreq}</changefreq>` : null,
      e.priority ? `    <priority>${e.priority}</priority>` : null,
      `  </url>`,
    ]
      .filter(Boolean)
      .join("\n"),
  );
  return [
    `<?xml version="1.0" encoding="UTF-8"?>`,
    `<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">`,
    ...urls,
    `</urlset>`,
  ].join("\n");
}

// Helper: bygg en sitemap-Response med explicita headers via Headers-objekt.
// Tidigare brydde plain object-headers ibland inte över i edge-runtime,
// vilket gav text/html på sitemaps → Google avvisade dem.
export function sitemapResponse(xml: string): Response {
  const headers = new Headers();
  headers.set("content-type", "application/xml; charset=utf-8");
  headers.set("cache-control", "public, max-age=3600, s-maxage=86400");
  headers.set("x-robots-tag", "noindex");
  return new Response(xml, { status: 200, headers });
}

// Behåll för bakåtkompatibilitet (men nya kallpunkter ska använda sitemapResponse).
export const SITEMAP_HEADERS = {
  "content-type": "application/xml; charset=utf-8",
  "cache-control": "public, max-age=3600, s-maxage=86400",
};

// VIKTIGT: utvärdera per request, inte vid modul-load. På Cloudflare Workers
// är `Date.now()` 0 (1970-01-01) tills första I/O sker, vilket annars läcker
// in i sitemap `<lastmod>`.
export const TODAY = (): string => new Date().toISOString().slice(0, 10);

// Sann lastmod. Sitemapprotokollet och Bing kräver att <lastmod> speglar när
// sidans huvudinnehåll faktiskt ändrades — inte när sitemapen genererades.
// Datumen kommer från git-historiken per routefil via
// scripts/gen-content-dates.ts. Saknas datum utelämnas <lastmod> hellre än
// att vi hittar på ett.
export function lastmodFor(path: string): string | undefined {
  const clean = path.length > 1 ? path.replace(/\/$/, "") : path;
  const exact = CONTENT_DATES[clean];
  if (exact) return exact;
  for (const [prefix, date] of Object.entries(CLUSTER_DATES)) {
    if (clean.startsWith(prefix)) return date;
  }
  return undefined;
}

/** Sätt sann lastmod på en lista sitemap-entries (befintlig lastmod vinner). */
export function withLastmod(entries: SitemapEntry[]): SitemapEntry[] {
  return entries.map((e) => {
    const lastmod = e.lastmod ?? lastmodFor(e.path);
    return lastmod ? { ...e, lastmod } : { ...e, lastmod: undefined };
  });
}
